import { supabase } from '../lib/supabase';

export const registrationService = {
  // Get all registrations for an event (admin/organizer only)
  async getEventRegistrations(eventId) {
    const { data, error } = await supabase?.from('event_registrations')?.select(`
        *,
        user:user_profiles!event_registrations_user_id_fkey(
          id,
          full_name,
          email,
          avatar_url
        ),
        event:events!event_registrations_event_id_fkey(
          id,
          title,
          event_date,
          event_time,
          max_participants
        ),
        approver:user_profiles!event_registrations_approved_by_fkey(
          id,
          full_name
        )
      `)?.eq('event_id', eventId)?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(reg => ({
      id: reg?.id,
      eventId: reg?.event_id,
      userId: reg?.user_id,
      status: reg?.status,
      participantName: reg?.participant_name,
      participantEmail: reg?.participant_email,
      participantPhone: reg?.participant_phone,
      equipmentRental: reg?.equipment_rental,
      specialRequirements: reg?.special_requirements,
      createdAt: reg?.created_at,
      updatedAt: reg?.updated_at,
      approvedAt: reg?.approved_at,
      approvedBy: reg?.approved_by,
      rejectionReason: reg?.rejection_reason,
      wantsLifeVest: reg?.wants_life_vest,
      wantsSupMotor: reg?.wants_sup_motor,
      wantsWaterproofBag: reg?.wants_waterproof_bag,
      wantsCancellationInsurance: reg?.wants_cancellation_insurance,
      havaleEftPaid: reg?.havale_eft_paid || false,
      paymentMethod: reg?.payment_method || null,
      selectedCustomOptions: reg?.selected_custom_options || [],
      totalPrice: reg?.total_price != null ? Number(reg?.total_price) : null,
      paytrLink: reg?.paytr_link || null,
      paymentStatus: reg?.payment_status || 'unpaid',
      ticketType: reg?.ticket_type || null,
      boardRented: reg?.board_rented || false,
      user: reg?.user ? {
        id: reg?.user?.id,
        fullName: reg?.user?.full_name,
        email: reg?.user?.email,
        avatarUrl: reg?.user?.avatar_url
      } : null,
      event: reg?.event ? {
        id: reg?.event?.id,
        title: reg?.event?.title,
        eventDate: reg?.event?.event_date,
        eventTime: reg?.event?.event_time,
        maxParticipants: reg?.event?.max_participants
      } : null,
      approver: reg?.approver ? {
        id: reg?.approver?.id,
        fullName: reg?.approver?.full_name
      } : null
    })) || [];
  },

  // Get all registrations for current user
  async getMyRegistrations() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('event_registrations')
      ?.select(`
        *,
        event:events!event_registrations_event_id_fkey(
          id,
          title,
          event_date,
          event_time,
          location,
          price_own_board,
          price_with_rental,
          images
        ),
        cancellation_processor:user_profiles!event_registrations_cancellation_processed_by_fkey(
          id,
          full_name
        )
      `)
      ?.eq('user_id', user?.id)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(reg => ({
      id: reg?.id,
      eventId: reg?.event_id,
      userId: reg?.user_id,
      status: reg?.status,
      participantName: reg?.participant_name,
      participantEmail: reg?.participant_email,
      participantPhone: reg?.participant_phone,
      equipmentRental: reg?.equipment_rental,
      specialRequirements: reg?.special_requirements,
      createdAt: reg?.created_at,
      updatedAt: reg?.updated_at,
      approvedAt: reg?.approved_at,
      rejectionReason: reg?.rejection_reason,
      cancellationRequestReason: reg?.cancellation_request_reason,
      cancellationRequestedAt: reg?.cancellation_requested_at,
      cancellationProcessedAt: reg?.cancellation_processed_at,
      cancellationProcessedBy: reg?.cancellation_processed_by,
      organizerCancellationResponse: reg?.organizer_cancellation_response,
      event: reg?.event ? {
        id: reg?.event?.id,
        title: reg?.event?.title,
        eventDate: reg?.event?.event_date,
        eventTime: reg?.event?.event_time,
        location: reg?.event?.location,
        priceOwnBoard: reg?.event?.price_own_board,
        priceWithRental: reg?.event?.price_with_rental,
        images: reg?.event?.images || []
      } : null,
      cancellationProcessor: reg?.cancellation_processor ? {
        id: reg?.cancellation_processor?.id,
        fullName: reg?.cancellation_processor?.full_name
      } : null
    })) || [];
  },

  // Check if current user is registered for a specific event
  async checkUserRegistration(eventId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      ?.from('event_registrations')
      ?.select('id, status')
      ?.eq('event_id', eventId)
      ?.eq('user_id', user?.id)
      ?.single();

    if (error || !data) return false;
    
    // User is registered if status is 'approved' or 'pending'
    return data?.status === 'approved' || data?.status === 'pending';
  },

  // Create new registration (user)
  async create(registrationData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if user already has a registration for this event
      const { data: existingRegistration, error: checkError } = await supabase
        ?.from('event_registrations')
        ?.select('id, status')
        ?.eq('event_id', registrationData?.eventId)
        ?.eq('user_id', user?.id)
        ?.maybeSingle();

      if (checkError) throw checkError;

      // If registration exists, handle based on status
      if (existingRegistration) {
        // For cancelled/rejected: UPDATE record to pending (allow re-registration)
        if (['cancelled', 'rejected']?.includes(existingRegistration?.status)) {
          const { data, error: updateError } = await supabase
            ?.from('event_registrations')
            ?.update({
              status: 'pending',
              participant_name: registrationData?.participantName,
              participant_email: registrationData?.participantEmail,
              participant_phone: registrationData?.participantPhone,
              equipment_rental: registrationData?.equipmentRental || false,
              special_requirements: registrationData?.specialRequirements || '',
              payment_method: registrationData?.paymentMethod || 'EFT/Havale',
              selected_custom_options: registrationData?.selectedCustomOptions || [],
              total_price: registrationData?.totalPrice || 0,
              // Reset cancellation fields for fresh start
              cancellation_request_reason: null,
              cancellation_requested_at: null,
              cancellation_processed_at: null,
              cancellation_processed_by: null,
              organizer_cancellation_response: null,
              rejection_reason: null,
              approved_at: null,
              approved_by: null
            })
            ?.eq('id', existingRegistration?.id)
            ?.select()
            ?.single();
          
          if (updateError) throw updateError;
          
          return {
            id: data?.id,
            eventId: data?.event_id,
            userId: data?.user_id,
            status: data?.status,
            participantName: data?.participant_name,
            participantEmail: data?.participant_email,
            participantPhone: data?.participant_phone,
            equipmentRental: data?.equipment_rental,
            specialRequirements: data?.special_requirements,
            createdAt: data?.created_at
          };
        } else {
          // For active statuses: prevent duplicate registration
          throw new Error('Bu etkinliğe zaten kayıtlısınız. Lütfen "Biletlerim" sayfasını kontrol ediniz.');
        }
      }

      // No existing registration - create new one
      const { data, error } = await supabase?.from('event_registrations')?.insert({
          event_id: registrationData?.eventId,
          user_id: user?.id,
          participant_name: registrationData?.participantName,
          participant_email: registrationData?.participantEmail,
          participant_phone: registrationData?.participantPhone,
          equipment_rental: registrationData?.equipmentRental || false,
          special_requirements: registrationData?.specialRequirements || '',
          wants_life_vest: registrationData?.wantsLifeVest || false,
          wants_sup_motor: registrationData?.wantsSupMotor || false,
          wants_waterproof_bag: registrationData?.wantsWaterproofBag || false,
          wants_cancellation_insurance: registrationData?.wantsCancellationInsurance || false,
          payment_method: registrationData?.paymentMethod || 'EFT/Havale',
          selected_custom_options: registrationData?.selectedCustomOptions || [],
          total_price: registrationData?.totalPrice || 0,
          ticket_type: registrationData?.ticketType || (registrationData?.equipmentRental ? 'rental_board' : 'own_board'),
          board_rented: registrationData?.boardRented !== undefined ? registrationData?.boardRented : (registrationData?.equipmentRental || false),
          status: 'pending'
        })?.select()?.single();

      if (error) throw error;

      // Broadcast notification to ALL admins
      try {
        const { data: adminUsers } = await supabase
          ?.from('user_profiles')
          ?.select('id')
          ?.eq('role', 'admin');

        if (adminUsers && adminUsers?.length > 0) {
          const eventTitle = registrationData?.eventTitle || 'Etkinlik';
          const participantName = registrationData?.participantName || 'Katılımcı';
          const totalPrice = registrationData?.totalPrice || 0;

          const adminNotifications = adminUsers?.map(admin => ({
            user_id: admin?.id,
            title: '🏄 Yeni Etkinlik Kaydı',
            message: `${participantName} — ${eventTitle} etkinliğine kayıt oldu. Tutar: ₺${Number(totalPrice)?.toLocaleString('tr-TR')}`,
            type: 'info',
            action_url: '/admin-dashboard',
            read: false,
          }));

          await supabase?.from('notifications')?.insert(adminNotifications);
        }
      } catch (notifErr) {
        console.error('Admin event registration notification error:', notifErr);
        // Non-blocking: don't throw
      }

      return {
        id: data?.id,
        eventId: data?.event_id,
        userId: data?.user_id,
        status: data?.status,
        participantName: data?.participant_name,
        participantEmail: data?.participant_email,
        participantPhone: data?.participant_phone,
        equipmentRental: data?.equipment_rental,
        specialRequirements: data?.special_requirements,
        wantsLifeVest: data?.wants_life_vest,
        wantsSupMotor: data?.wants_sup_motor,
        wantsWaterproofBag: data?.wants_waterproof_bag,
        wantsCancellationInsurance: data?.wants_cancellation_insurance,
        createdAt: data?.created_at
      };
    } catch (error) {
      // Handle SQL unique constraint violation (duplicate registration)
      if (error?.code === '23505') {
        throw new Error('Bu etkinliğe zaten kayıtlısınız. Lütfen "Biletlerim" sayfasını kontrol ediniz.');
      }
      // Re-throw other errors
      throw error;
    }
  },

  // Approve registration (admin/organizer)
  async approve(registrationId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('event_registrations')?.update({
        status: 'approved',
        approved_at: new Date()?.toISOString(),
        approved_by: user?.id
      })?.eq('id', registrationId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      status: data?.status,
      approvedAt: data?.approved_at,
      approvedBy: data?.approved_by
    };
  },

  // Reject registration (admin/organizer)
  async reject(registrationId, reason) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('event_registrations')?.update({
        status: 'rejected',
        rejection_reason: reason || '',
        approved_by: user?.id
      })?.eq('id', registrationId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      status: data?.status,
      rejectionReason: data?.rejection_reason
    };
  },

  // Cancel registration (user) - Updated to use cancellation request flow
  async requestCancellation(registrationId, reason = '') {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // First, fetch the registration with event details to check cancellation window
    const { data: registration, error: fetchError } = await supabase
      ?.from('event_registrations')
      ?.select(`
        *,
        event:events!event_registrations_event_id_fkey(
          event_date,
          event_time,
          title,
          organizer_id
        ),
        organizer:events!event_registrations_event_id_fkey(
          organizer:user_profiles!events_organizer_id_fkey(
            full_name,
            email
          )
        )
      `)
      ?.eq('id', registrationId)
      ?.eq('user_id', user?.id)
      ?.single();

    if (fetchError) throw fetchError;
    if (!registration) throw new Error('Kayıt bulunamadı');

    // Check if cancellation is allowed (24 hours before event)
    const eventDateTime = new Date(`${registration?.event?.event_date}T${registration?.event?.event_time}`);
    const now = new Date();
    const hoursUntilEvent = (eventDateTime - now) / (1000 * 60 * 60);

    if (hoursUntilEvent < 24) {
      throw new Error('Etkinliğe 24 saatten az kaldığı için iptal talebi oluşturulamaz');
    }

    if (registration?.status !== 'approved') {
      throw new Error('Sadece onaylanmış rezervasyonlar için iptal talebi oluşturulabilir');
    }

    // CRITICAL FIX: Don't change status - keep it as 'approved'
    // Only set cancellation_requested_at and reason to track the request
    const { data, error } = await supabase
      ?.from('event_registrations')
      ?.update({
        cancellation_request_reason: reason,
        cancellation_requested_at: new Date()?.toISOString()
      })
      ?.eq('id', registrationId)
      ?.eq('user_id', user?.id)
      ?.select()
      ?.single();

    if (error) throw error;

    // Send email notification to organizer
    try {
      const organizerEmail = registration?.organizer?.organizer?.email;
      if (organizerEmail) {
        await supabase?.functions?.invoke('send-email', {
          body: {
            to: organizerEmail,
            subject: `Yeni İptal Talebi - ${registration?.event?.title}`,
            participantName: registration?.participant_name,
            eventTitle: registration?.event?.title,
            organizerNotes: reason,
            type: 'cancellation_request'
          }
        });
      }
    } catch (emailError) {
      console.error('Failed to send email notification to organizer:', emailError);
      // Don't throw error - cancellation request was created successfully
    }

    return {
      id: data?.id,
      status: data?.status,
      cancellationRequestedAt: data?.cancellation_requested_at,
      cancellationRequestReason: data?.cancellation_request_reason
    };
  },

  // Cancel approved registration (admin/organizer)
  async cancelApprovedRegistration(registrationId, reason = '') {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Update registration to cancelled status
    const { data, error } = await supabase
      ?.from('event_registrations')
      ?.update({
        status: 'cancelled',
        organizer_cancellation_response: reason,
        cancellation_processed_at: new Date()?.toISOString(),
        cancellation_processed_by: user?.id
      })
      ?.eq('id', registrationId)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      status: data?.status,
      cancellationProcessedAt: data?.cancellation_processed_at,
      cancellationProcessedBy: data?.cancellation_processed_by,
      organizerCancellationResponse: data?.organizer_cancellation_response
    };
  },

  // Get registration counts by status for an event
  async getRegistrationStats(eventId) {
    const { data, error } = await supabase?.from('event_registrations')?.select('status')?.eq('event_id', eventId);

    if (error) throw error;

    // Active statuses: pending, approved, paid — exclude cancelled and rejected
    const activeStatuses = ['pending', 'approved', 'paid'];

    // Helper: each row counts as 1 participant
    const getCount = () => 1;

    const stats = {
      total: data?.length || 0,
      pending: data?.filter(r => r?.status === 'pending')?.reduce((sum) => sum + getCount(), 0) || 0,
      approved: data?.filter(r => r?.status === 'approved')?.reduce((sum) => sum + getCount(), 0) || 0,
      paid: data?.filter(r => r?.status === 'paid')?.reduce((sum) => sum + getCount(), 0) || 0,
      rejected: data?.filter(r => r?.status === 'rejected')?.reduce((sum) => sum + getCount(), 0) || 0,
      cancelled: data?.filter(r => r?.status === 'cancelled')?.reduce((sum) => sum + getCount(), 0) || 0,
      cancellationRequested: data?.filter(r => r?.status === 'cancellation_requested')?.reduce((sum) => sum + getCount(), 0) || 0,
      // Active registered count: pending + approved + paid (excludes cancelled/rejected)
      registered: data?.filter(r => activeStatuses?.includes(r?.status))?.reduce((sum) => sum + getCount(), 0) || 0,
    };

    return stats;
  },

  /**
   * Check if cancellation is allowed based on event date
   */
  canCancelRegistration(eventStartDate) {
    const eventDate = new Date(eventStartDate);
    const now = new Date();
    const hoursDifference = (eventDate - now) / (1000 * 60 * 60);
    
    // Allow cancellation if event is more than 24 hours away
    return hoursDifference > 24;
  }
};